import React, { useMemo } from "react";
import Highlighter from "react-highlight-words";

import {
  HIGHLIGHT_OPTIONS,
  buildSingleDocumentIndex,
  toHighlightWords,
} from "./utils/searchOptions";

const HighlightMark: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <mark className="rounded-sm bg-highlight px-0.5 text-highlight-foreground">{children}</mark>
);

export interface HighlightTextProps {
  text: string;
  highlightQuery: string;
}

export const HighlightText: React.FC<HighlightTextProps> = ({ text, highlightQuery }) => {
  const query = highlightQuery?.trim() ?? "";

  const highlightWords = useMemo(() => {
    if (!query || !text) return [];
    const index = buildSingleDocumentIndex(text);
    const [result] = index.search(query, HIGHLIGHT_OPTIONS);
    return result ? toHighlightWords(result.terms, query) : [];
  }, [query, text]);

  const findChunks = useMemo(
    () =>
      ({ textToHighlight }: { textToHighlight: string }) => {
        const chunks: { start: number; end: number }[] = [];
        for (const { text: word, isPrefixMatch } of highlightWords) {
          if (!word) continue;
          const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const pattern = isPrefixMatch ? `\\b${escaped}` : `\\b${escaped}\\b`;
          const regex = new RegExp(pattern, "gi");
          let match: RegExpExecArray | null;
          while ((match = regex.exec(textToHighlight))) {
            chunks.push({ start: match.index, end: match.index + word.length });
          }
        }
        return chunks;
      },
    [highlightWords]
  );

  if (!query) return <>{text}</>;

  return (
    <Highlighter
      searchWords={highlightWords.map((word) => word.text)}
      autoEscape
      textToHighlight={text}
      highlightTag={HighlightMark}
      findChunks={findChunks}
    />
  );
};
